import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, Link2, Loader2, RefreshCcw, ExternalLink } from "lucide-react";
import { api, API } from "../lib/api";

export default function IntegrationsGoogle() {
  const [status, setStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [areas, setAreas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);

  const loadAll = async () => {
    try {
      const [s, c, a, l] = await Promise.all([
        fetch(`${API}/integrations/google/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("pxos.token")}` }
        }).then((r) => r.json()),
        fetch(`${API}/integrations/google/calendars`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("pxos.token")}` }
        }).then((r) => r.json()),
        api.listAreas(),
        fetch(`${API}/integrations/google/sync-logs?limit=10`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("pxos.token")}` }
        }).then((r) => r.json()),
      ]);
      setStatus(s); setCalendars(Array.isArray(c) ? c : []);
      setAreas(a); setSyncLogs(Array.isArray(l) ? l : []);
    } catch (e) { toast.error("Errore nel caricamento"); }
  };
  useEffect(() => { loadAll(); }, []);

  const stepUp = async () => {
    const pwd = prompt("Conferma la tua password per continuare (step-up):");
    if (!pwd) return null;
    try {
      const r = await api.stepupPassword(pwd);
      return r.stepup_token || r.token;
    } catch { toast.error("Password errata"); return null; }
  };

  const connect = async () => {
    if (!status?.configured) { toast.error("Google Calendar non è configurato lato server."); return; }
    setConnecting(true);
    try {
      const tok = await stepUp();
      if (!tok) { setConnecting(false); return; }
      const r = await fetch(`${API}/integrations/google/connect`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("pxos.token")}`,
          "X-Stepup-Token": tok,
          "Content-Type": "application/json",
        },
      }).then((r) => r.json());
      if (r.authorization_url) window.location.href = r.authorization_url;
      else toast.error(r.detail?.message || r.detail || "Errore avvio OAuth");
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    if (!confirm("Scollegare l'account Google? Gli eventi già importati rimarranno in app.")) return;
    const tok = await stepUp(); if (!tok) return;
    try {
      const r = await fetch(`${API}/integrations/google/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("pxos.token")}`, "X-Stepup-Token": tok },
      });
      if (r.ok) { toast.success("Scollegato"); loadAll(); }
      else toast.error("Errore");
    } catch { toast.error("Errore"); }
  };

  const patchCal = async (id, patch) => {
    setBusy(true);
    try {
      await fetch(`${API}/integrations/google/calendars/${id}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("pxos.token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(patch),
      });
      loadAll();
    } finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/integrations/google/sync-now`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("pxos.token")}` },
      }).then((r) => r.json());
      toast(r.note || "Sync avviata");
      loadAll();
    } finally { setBusy(false); }
  };

  if (!status) return <div className="pxos-card p-6 opacity-70">Caricamento…</div>;

  return (
    <section className="space-y-6" data-testid="integrations-google">
      <div>
        <h2 className="font-display text-xl font-semibold flex items-center gap-2">
          <Link2 size={18}/> Google Calendar
        </h2>
        <p className="text-sm opacity-70 mt-1 max-w-2xl">
          Sincronizzazione bidirezionale reale. Eventi Google → agenda dell'app. Task/scadenze con "Mostra su Google" attivo → calendario Google.
        </p>
      </div>

      {!status.configured && (
        <div className="pxos-card p-4 border-l-4 border-orange-400 bg-orange-50" data-testid="google-not-configured">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-orange-600 shrink-0 mt-0.5" size={20}/>
            <div className="min-w-0">
              <h3 className="font-semibold text-orange-900">Google Calendar non configurato</h3>
              <p className="text-sm text-orange-800 mt-1">
                Le variabili <code className="font-mono text-xs bg-orange-100 px-1 rounded">GOOGLE_OAUTH_CLIENT_ID</code>,
                <code className="font-mono text-xs bg-orange-100 px-1 rounded ml-1">GOOGLE_OAUTH_CLIENT_SECRET</code>,
                <code className="font-mono text-xs bg-orange-100 px-1 rounded ml-1">GOOGLE_OAUTH_REDIRECT_URI</code>
                e <code className="font-mono text-xs bg-orange-100 px-1 rounded ml-1">CALENDAR_TOKEN_ENCRYPTION_KEY</code>
                devono essere impostate in <code className="font-mono text-xs">backend/.env</code>.
              </p>
              <p className="text-sm text-orange-800 mt-2">
                Segui la guida completa: <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">Google Cloud Console <ExternalLink size={12}/></a>
                oppure apri <code className="font-mono text-xs bg-orange-100 px-1 rounded">/app/memory/google_oauth_setup.md</code>.
              </p>
              <ol className="mt-3 space-y-1 text-sm text-orange-900 list-decimal list-inside">
                <li>Crea un progetto su Google Cloud Console.</li>
                <li>Abilita "Google Calendar API".</li>
                <li>Configura la schermata di consenso OAuth (External).</li>
                <li>Crea "OAuth 2.0 Client ID" tipo "Web application".</li>
                <li>Aggiungi come redirect URI: <code className="font-mono text-xs">{window.location.origin}/api/integrations/google/callback</code>.</li>
                <li>Copia Client ID + Secret nel <code className="font-mono text-xs">.env</code> e riavvia il backend.</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {status.configured && !status.connected && (
        <div className="pxos-card p-6 flex items-center justify-between gap-4" data-testid="google-disconnected">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <XCircle size={18} className="text-gray-500"/>
              <span className="font-medium">Nessun account collegato</span>
            </div>
            <p className="text-sm opacity-70">Clicca sotto per autorizzare l'accesso al tuo Google Calendar.</p>
          </div>
          <button onClick={connect} disabled={connecting}
                  className="pxos-btn-primary inline-flex items-center gap-2" data-testid="google-connect-btn">
            {connecting ? <Loader2 className="animate-spin" size={14}/> : <Link2 size={14}/>}
            Collega Google Calendar
          </button>
        </div>
      )}

      {status.connected && (
        <div className="space-y-4">
          <div className="pxos-card p-5" data-testid="google-connected">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-green-600" size={22}/>
                <div>
                  <div className="font-semibold" data-testid="google-account-email">{status.account_email}</div>
                  <div className="text-xs opacity-70">Collegato il {status.connected_at ? new Date(status.connected_at).toLocaleDateString("it-IT") : "—"}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={syncNow} disabled={busy} className="rounded-full border px-3 py-1.5 text-sm inline-flex items-center gap-1" data-testid="google-sync-now">
                  <RefreshCcw size={14}/> Sincronizza tutto ora
                </button>
                <button onClick={disconnect} className="rounded-full border border-red-300 text-red-700 px-3 py-1.5 text-sm" data-testid="google-disconnect-btn">
                  Scollega account
                </button>
              </div>
            </div>
            <div className="mt-3 text-xs opacity-70">
              Ultimo sync: {status.last_sync_at ? new Date(status.last_sync_at).toLocaleString("it-IT") : "mai"} · Calendari sincronizzati: {status.calendars_selected}
            </div>
          </div>

          <div className="pxos-card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b bg-white/60" style={{ borderColor: "var(--pxos-border)" }}>
              <h3 className="font-display text-base font-semibold">I tuoi calendari</h3>
              <p className="text-xs opacity-70">Seleziona quali sincronizzare e a quale area associarli.</p>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
              {calendars.length === 0 && <div className="p-6 text-center opacity-70">Nessun calendario.</div>}
              {calendars.map((c) => (
                <div key={c.id} className="p-4 flex flex-wrap items-center gap-3" data-testid="google-calendar-row">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: c.color_hex || "#B8B8B8" }}/>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate" data-testid="google-calendar-summary">{c.summary}</div>
                    <div className="text-xs opacity-70">{c.timezone || "—"} {c.is_primary ? "· primary" : ""}</div>
                  </div>
                  <label className="text-xs flex items-center gap-1.5">
                    <input type="checkbox" checked={c.is_selected_for_sync}
                           onChange={(e) => patchCal(c.id, { is_selected_for_sync: e.target.checked })}
                           data-testid="google-calendar-toggle-sync"/>
                    Sincronizza
                  </label>
                  <select className="pxos-input text-xs w-40" value={c.linked_life_area_id || ""}
                          onChange={(e) => patchCal(c.id, { linked_life_area_id: e.target.value || null })}
                          data-testid="google-calendar-area">
                    <option value="">Nessuna area</option>
                    {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                  <select className="pxos-input text-xs w-44" value={c.sync_direction}
                          onChange={(e) => patchCal(c.id, { sync_direction: e.target.value })}
                          data-testid="google-calendar-direction">
                    <option value="two_way">Bidirezionale</option>
                    <option value="from_provider_only">Solo da Google</option>
                    <option value="to_provider_only">Solo verso Google</option>
                    <option value="disabled">Disabilitato</option>
                  </select>
                </div>
              ))}
            </div>
          </div>

          {syncLogs.length > 0 && (
            <div className="pxos-card p-0 overflow-hidden">
              <div className="px-4 py-3 border-b bg-white/60" style={{ borderColor: "var(--pxos-border)" }}>
                <h3 className="font-display text-base font-semibold">Ultime sincronizzazioni</h3>
              </div>
              <div className="divide-y text-sm" style={{ borderColor: "var(--pxos-border)" }}>
                {syncLogs.map((l) => (
                  <div key={l.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid="google-sync-log-row">
                    <span className={`pxos-badge text-[10px] ${l.status === "success" ? "text-green-800" : l.status === "failed" ? "text-red-800" : ""}`}>
                      {l.status}
                    </span>
                    <span className="text-xs opacity-70">{new Date(l.started_at).toLocaleString("it-IT")}</span>
                    <span className="text-xs opacity-70">
                      import {l.events_imported} · aggiornati {l.events_updated} · eliminati {l.events_deleted} · push {l.events_pushed}
                      {l.conflicts_detected > 0 && ` · conflitti ${l.conflicts_detected}`}
                    </span>
                    {l.error_message && <span className="text-xs text-red-700 truncate">{l.error_message}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
